// Top-level App component for the WebUI MVP.
//
// v0.3.2: Manual calculation is the default and primary workflow.
// File import is preserved as a collapsible "高级导入" panel below
// the manual form so existing adapters and the help popup remain
// usable without crowding the main flow.

import React, { useState } from "react";
import {
  Alert,
  Collapse,
  ConfigProvider,
  Layout,
  Space,
  Typography,
  theme,
} from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import zhCN from "antd/locale/zh_CN";

import { FileUpload } from "./components/FileUpload.js";
import { ManualEntryPage } from "./components/ManualEntryPage.js";
import { ResultView } from "./components/ResultView.js";
import { buildInlineMarkdown } from "./adapters/buildInlineMarkdown.js";
import type { RunFileOutput } from "./adapters/runFile.js";

const { Header, Content, Footer } = Layout;
const { Title, Paragraph } = Typography;

export function App(): JSX.Element {
  // File-import state lives here so the advanced-import panel can
  // render the imported result via the existing ResultView. The
  // manual page is the primary flow and is rendered unconditionally.
  const [imported, setImported] = useState<{
    output: RunFileOutput;
    fileName: string;
    markdown: string;
  } | null>(null);

  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        algorithm: theme.defaultAlgorithm,
        token: { colorPrimary: "#1677ff" },
      }}
    >
      <Layout style={{ minHeight: "100vh" }}>
        <Header
          style={{
            background: "#fff",
            paddingInline: 24,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Title level={3} style={{ margin: 0 }}>
            深圳市地价测算可视化工具
          </Title>
          <Paragraph type="secondary" style={{ margin: 0, fontSize: 12 }}>
            直接在下方表格中填写宗地与建筑面积分项信息，点击「执行测算」即可得到地价结果。
          </Paragraph>
        </Header>
        <Content style={{ padding: 24 }}>
          <Space direction="vertical" size={24} style={{ width: "100%" }}>
            <Alert
              type="info"
              showIcon
              message="默认流程：在表格中填写后点击「执行测算」"
              description="按顺序填写宗地基础信息、市场价格、（可选）来源说明与建筑面积分项；新增 / 删除 / 编辑分项均支持。校验消息会出现在结果区。"
            />
            <ManualEntryPage />
            <Collapse
              ghost
              items={[
                {
                  key: "advanced-import",
                  label: (
                    <Space>
                      <FileTextOutlined />
                      <span>高级导入：从 .json / .csv / .xlsx 文件导入</span>
                    </Space>
                  ),
                  children: (
                    <Space direction="vertical" size={8} style={{ width: "100%" }}>
                      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                        仅在批量导入已有测算数据时使用。上传文件后会在本区域显示测算结果与下载按钮，不会回到手动测算表单。
                      </Paragraph>
                      <FileUpload
                        onResult={(out, fileName) => {
                          const md = buildInlineMarkdown(out);
                          setImported({ output: out, fileName, markdown: md });
                        }}
                      />
                      {imported && (
                        <ResultView
                          result={imported.output.result}
                          sourceFileName={imported.fileName}
                          inlineMarkdown={imported.markdown}
                        />
                      )}
                    </Space>
                  ),
                },
              ]}
            />
          </Space>
        </Content>
        <Footer style={{ textAlign: "center", fontSize: 12 }}>
          测算结果由核心引擎生成；本工具不抓取 / 不调用官方查询网站。
        </Footer>
      </Layout>
    </ConfigProvider>
  );
}